/**
 * Gamepad-gombok konfigurációja. Egy funkcióhoz több gomb is rendelhető
 * (pl. gáz = A + RT), egy gomb viszont csak egy funkcióhoz tartozhat.
 */

export type PadAction = 'steerLeft' | 'steerRight' | 'throttle' | 'brake' | 'drift' | 'item' | 'ability';

export interface PadActionDef {
  id: PadAction;
  label: string;
  hint: string;
}

export const PAD_ACTIONS: readonly PadActionDef[] = [
  { id: 'throttle', label: 'Gáz', hint: 'A, RT, D-pad fel' },
  { id: 'brake', label: 'Fék', hint: 'B, LT, D-pad le' },
  { id: 'steerLeft', label: 'Kormány balra', hint: 'D-pad bal' },
  { id: 'steerRight', label: 'Kormány jobbra', hint: 'D-pad jobb' },
  { id: 'drift', label: 'Drift', hint: 'LB / RB' },
  { id: 'item', label: 'Tárgy', hint: 'X' },
  { id: 'ability', label: 'Képesség', hint: 'Y' },
];

/** Standard gamepad gombnevek (Xbox-sorrend, zárójelben a nyers index). */
export const PAD_BUTTON_LABELS: readonly { index: number; label: string }[] = [
  { index: 0, label: 'A' },
  { index: 1, label: 'B' },
  { index: 2, label: 'X' },
  { index: 3, label: 'Y' },
  { index: 4, label: 'LB' },
  { index: 5, label: 'RB' },
  { index: 6, label: 'LT' },
  { index: 7, label: 'RT' },
  { index: 8, label: 'Back' },
  { index: 9, label: 'Start' },
  { index: 10, label: 'LS' },
  { index: 11, label: 'RS' },
  { index: 12, label: 'D-pad ↑' },
  { index: 13, label: 'D-pad ↓' },
  { index: 14, label: 'D-pad ←' },
  { index: 15, label: 'D-pad →' },
  { index: 16, label: 'Guide' },
];

export type PadBindings = Record<PadAction, number[]>;

export const DEFAULT_PAD_BINDINGS: PadBindings = {
  throttle: [0, 7, 12],
  brake: [1, 6, 13],
  steerLeft: [14],
  steerRight: [15],
  drift: [4, 5],
  item: [2],
  ability: [3],
};

const STORAGE_KEY = 'csf.gamepad.v1';
const MAX_BUTTON_INDEX = 31;

const cloneDefaults = (): PadBindings => ({
  throttle: [...DEFAULT_PAD_BINDINGS.throttle],
  brake: [...DEFAULT_PAD_BINDINGS.brake],
  steerLeft: [...DEFAULT_PAD_BINDINGS.steerLeft],
  steerRight: [...DEFAULT_PAD_BINDINGS.steerRight],
  drift: [...DEFAULT_PAD_BINDINGS.drift],
  item: [...DEFAULT_PAD_BINDINGS.item],
  ability: [...DEFAULT_PAD_BINDINGS.ability],
});

const isButton = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= MAX_BUTTON_INDEX;

const sanitize = (value: unknown, fallback: number[]): number[] => {
  if (!Array.isArray(value)) return [...fallback];
  const out: number[] = [];
  for (const item of value) {
    if (isButton(item) && !out.includes(item)) out.push(item);
  }
  return out;
};

const read = (): PadBindings => {
  const out = cloneDefaults();
  if (typeof window === 'undefined') return out;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return out;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return out;
    const source = parsed as Partial<Record<PadAction, unknown>>;
    for (const action of PAD_ACTIONS) {
      out[action.id] = sanitize(source[action.id], DEFAULT_PAD_BINDINGS[action.id]);
    }
    // egy gomb csak egy funkcióé legyen
    const seen = new Set<number>();
    for (const action of PAD_ACTIONS) {
      out[action.id] = out[action.id].filter((index) => {
        if (seen.has(index)) return false;
        seen.add(index);
        return true;
      });
    }
  } catch {
    /* privát mód / hibás JSON: maradnak a defaultok */
  }
  return out;
};

/** Az aktuális, élő gamepad-kiosztás (a UI módosítja, az InputManager olvassa). */
export const padBindings: PadBindings = read();

export function savePadBindings(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(padBindings));
  } catch {
    /* csendben elmarad */
  }
}

export function setPadBindings(action: PadAction, buttons: number[]): void {
  padBindings[action] = sanitize(buttons, []);
  savePadBindings();
}

/** Gomb hozzáadása; ha máshol volt, onnan áthelyezi. */
export function addPadButton(action: PadAction, index: number): void {
  if (!isButton(index)) return;
  for (const def of PAD_ACTIONS) {
    const list = padBindings[def.id];
    const at = list.indexOf(index);
    if (at >= 0) list.splice(at, 1);
  }
  if (!padBindings[action].includes(index)) padBindings[action].push(index);
  savePadBindings();
}

export function removePadButton(action: PadAction, index: number): void {
  const list = padBindings[action];
  const at = list.indexOf(index);
  if (at >= 0) list.splice(at, 1);
  savePadBindings();
}

export function resetPadBindings(): void {
  const defaults = cloneDefaults();
  for (const def of PAD_ACTIONS) {
    padBindings[def.id] = defaults[def.id];
  }
  savePadBindings();
}

export function padButtonLabel(index: number): string {
  return PAD_BUTTON_LABELS.find((button) => button.index === index)?.label ?? `Gomb ${index}`;
}
